// Profile Page - Clubhouse 2.0 Design
// Complete redesign with ProfileHero, enhanced BadgeWall, collapsible sections, and animations

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { ProfileHero } from "@/components/profile/ProfileHero";
import { BadgeWall } from "@/components/profile/BadgeWall";
// import { ProfilePrivacySettings } from "@/features/clubhouse/components/ProfilePrivacySettings";
import { getProfileStats } from "@/services/profile-stats";
import { 
  Settings, 
  Moon, 
  Sun, 
  LogOut, 
  Video, 
  Shield, 
  ChevronRight,
  Target,
  CheckCircle2,
  Plus,
  Crown,
  Award,
  Globe,
  Users
} from "lucide-react";

interface UserProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  handicap: number | null;
  skill_level?: 'beginner' | 'intermediate' | 'advanced' | 'low_handicap' | 'scratch_plus' | null;
  created_at: string;
  updated_at: string;
  // P05-4-1: Coach fields
  is_coach?: boolean;
  verified?: boolean;
  coach_bio?: string | null;
  specialties?: string[] | null;
  certifications?: string[] | null;
  website_url?: string | null;
}

interface UserSubscription {
  plan_name: string;
  status: string;
  current_period_end: string | null;
}

interface UserUsage {
  monthly_analyses_used: number;
}

interface Goal {
  id: string;
  text: string;
  completed: boolean;
  progress: number;
}

export default function Profile() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [darkMode, setDarkMode] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [goalsExpanded, setGoalsExpanded] = useState(true);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  // Fetch profile stats using React Query
  const { data: stats } = useQuery({
    queryKey: ['profile', 'stats', user?.id],
    queryFn: () => getProfileStats(user!.id),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    if (!user) return;

    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching profile:', profileError);
      } else {
        setProfile(profileData);
      }

      // Fetch subscription
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('plan_name, status, current_period_end')
        .eq('user_id', user.id)
        .single();

      if (subscriptionError && subscriptionError.code !== 'PGRST116') {
        console.error('Error fetching subscription:', subscriptionError);
      } else {
        setSubscription(subscriptionData);
      }

      // Fetch usage
      const { data: usageData, error: usageError } = await supabase
        .from('current_usage')
        .select('monthly_analyses_used')
        .eq('user_id', user.id)
        .single();

      if (usageError && usageError.code !== 'PGRST116') {
        console.error('Error fetching usage:', usageError);
      } else {
        setUsage(usageData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
      });
    } catch (error) {
      toast({
        title: "Error signing out",
        description: "There was an error signing out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle("dark");
  };

  const formatMemberSince = (dateString: string | undefined) => {
    if (!dateString) return "Recently joined";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Sample goals data (hardcoded for now)
  const goals: Goal[] = [
    { id: '1', text: 'Fix slice issues', completed: false, progress: 65 },
    { id: '2', text: 'Eliminate fat shots', completed: false, progress: 40 },
    { id: '3', text: 'Improve putting accuracy', completed: true, progress: 100 },
    { id: '4', text: 'Break 80 consistently', completed: false, progress: 25 },
  ];

  // Determine tier from subscription
  const tier = subscription?.plan_name === 'pro' ? 'pro' : 
               subscription?.plan_name === 'elite' ? 'elite' : 'free';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* ProfileHero */}
        <ProfileHero
          name={profile?.full_name || user?.email || 'Golf Player'}
          memberSince={formatMemberSince(profile?.created_at)}
          tier={tier}
          stats={stats || {
            swingsAnalyzed: 0,
            drillsCompleted: 0,
            currentStreak: 0,
            badgesEarned: 0,
          }}
        />

        {/* P05-4-1: Coach Badge & Info */}
        {profile?.is_coach && profile?.verified && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <CardTitle>Verified Coach</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.coach_bio && (
                  <p className="text-sm text-muted-foreground">{profile.coach_bio}</p>
                )}
                {profile.specialties && profile.specialties.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Specialties</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.specialties.map((spec) => (
                        <Badge key={spec} variant="secondary">{spec}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {profile.certifications && profile.certifications.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Certifications</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.certifications.map((cert) => (
                        <Badge key={cert} variant="outline">{cert}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {profile.website_url && (
                  <a
                    href={profile.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-emerald-600 hover:underline"
                  >
                    <Globe className="w-4 h-4" />
                    Visit website
                  </a>
                )}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/coach-dashboard")}
                >
                  View Coach Dashboard
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Subscription Upgrade Card (if free tier) */}
        {tier === 'free' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card className="bg-[#FEF3C7] border-[#F9C94E]/30 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-5 h-5 text-[#F9C94E]" />
                      <h3 className="font-semibold text-foreground">Unlock Your Full Potential</h3>
          </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Get unlimited swing analysis, personalized coaching, and exclusive clubhouse features
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span>• Unlimited swings</span>
                      <span>• Priority coach feedback</span>
                      <span>• Advanced analytics</span>
        </div>
      </div>
                  <Button className="bg-gradient-to-r from-[#F9C94E] to-[#EAB308] text-[#0D382A] hover:shadow-lg whitespace-nowrap font-semibold">
                    Upgrade to Pro
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* BadgeWall */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <BadgeWall />
        </motion.div>

        {/* Goals Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Collapsible open={goalsExpanded} onOpenChange={setGoalsExpanded}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#196644]" />
                      <CardTitle>Your Goals</CardTitle>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${goalsExpanded ? 'rotate-90' : ''}`} />
            </div>
                  <CardDescription>Track your improvement journey</CardDescription>
                </CardHeader>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {goals.map((goal, index) => (
                    <motion.div
                      key={goal.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="space-y-2"
                    >
            <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            goal.completed 
                              ? 'bg-[#3DC68D] text-white' 
                              : 'border-2 border-gray-300'
                          }`}>
                            {goal.completed && <CheckCircle2 className="w-3 h-3" />}
              </div>
                          <span className={`text-sm ${goal.completed ? 'line-through text-muted-foreground' : ''}`}>
                            {goal.text}
                          </span>
            </div>
                        <span className="text-xs font-medium text-muted-foreground">
                          {goal.progress}%
                        </span>
      </div>
                      {!goal.completed && (
                        <Progress value={goal.progress} className="h-1.5" />
                      )}
                    </motion.div>
                  ))}

                  <Button variant="outline" size="sm" className="w-full mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Goal
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </motion.div>

        {/* Skill Level & Handicap Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.35 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Golf Profile</CardTitle>
              <CardDescription>Your skill level and handicap help personalize your coaching</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Skill Level */}
              <div className="space-y-2">
                <Label htmlFor="skill_level">Skill Level</Label>
                <Select
                  value={profile?.skill_level || 'beginner'}
                  onValueChange={async (value) => {
                    if (!user || !profile) return;
                    const { error } = await supabase
                      .from('profiles')
                      .update({ skill_level: value })
                      .eq('user_id', user.id);
                    if (error) {
                      toast({
                        title: 'Error',
                        description: 'Failed to update skill level',
                        variant: 'destructive',
                      });
                    } else {
                      setProfile({ ...profile, skill_level: value as any });
                      toast({
                        title: 'Success',
                        description: 'Skill level updated',
                      });
                    }
                  }}
                >
                  <SelectTrigger id="skill_level">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner (20+ handicap)</SelectItem>
                    <SelectItem value="intermediate">Intermediate (10-19 handicap)</SelectItem>
                    <SelectItem value="advanced">Advanced (5-9 handicap)</SelectItem>
                    <SelectItem value="low_handicap">Low Handicap (0-4 handicap)</SelectItem>
                    <SelectItem value="scratch_plus">Scratch Plus (+2 to 2 handicap)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Handicap */}
              <div className="space-y-2">
                <Label htmlFor="handicap">Handicap (Optional)</Label>
                <div className="flex gap-2">
                  <Input
                    id="handicap"
                    type="number"
                    placeholder="e.g., 12"
                    value={profile?.handicap ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                        setProfile({ ...profile!, handicap: value === '' ? null : parseFloat(value) });
                      }
                    }}
                    onBlur={async () => {
                      if (!user || !profile) return;
                      const { error } = await supabase
                        .from('profiles')
                        .update({ handicap: profile.handicap })
                        .eq('user_id', user.id);
                      if (error) {
                        toast({
                          title: 'Error',
                          description: 'Failed to update handicap',
                          variant: 'destructive',
                        });
                      } else {
                        toast({
                          title: 'Success',
                          description: 'Handicap updated',
                        });
                      }
                    }}
                    min="-10"
                    max="36"
                    step="0.1"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter your current handicap. Use negative numbers for plus handicaps (e.g., -2 for +2).
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Settings Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Collapsible open={settingsExpanded} onOpenChange={setSettingsExpanded}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-muted-foreground" />
                      <CardTitle>Settings & Preferences</CardTitle>
        </div>
                    <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${settingsExpanded ? 'rotate-90' : ''}`} />
      </div>
                </CardHeader>
              </CollapsibleTrigger>
        
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                      {darkMode ? (
                        <Moon className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <Sun className="w-5 h-5 text-muted-foreground" />
                      )}
                <div>
                        <p className="font-medium">Dark Mode</p>
                        <p className="text-sm text-muted-foreground">
                    Toggle between light and dark themes
                        </p>
                      </div>
                    </div>
                    <Switch checked={darkMode} onCheckedChange={toggleDarkMode} />
                  </div>

                  <Separator />

                  {/* Privacy Settings */}
                  <Button variant="ghost" className="w-full justify-start">
                    <Shield className="w-5 h-5 mr-3" />
                    Privacy & Visibility
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </Button>

                  {/* Account Settings */}
                  <Button variant="ghost" className="w-full justify-start">
                    <Settings className="w-5 h-5 mr-3" />
                Account Settings
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </motion.div>

        {/* Privacy Settings (non-collapsible) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.45 }}
        >
          {/* <ProfilePrivacySettings /> */}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="space-y-3"
        >
          <Button 
            variant="outline" 
            className="w-full justify-start" 
            size="lg"
            onClick={() => navigate("/library")}
          >
            <Video className="w-5 h-5 mr-3" />
            View Video Library
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Button>

          {user?.email?.endsWith('@nodoublesgolfco.com') && (
            <Button 
              variant="outline" 
              className="w-full justify-start border-blue-500 text-blue-600 hover:bg-blue-50" 
              size="lg"
              onClick={() => navigate("/admin/learning-stages")}
            >
              <Shield className="w-5 h-5 mr-3" />
              Admin Dashboard
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
          )}

          <Button
          variant="outline" 
            className="w-full justify-start text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
            size="lg"
          onClick={handleSignOut}
        >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </motion.div>

        {/* Footer Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.6 }}
          className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground pt-4"
        >
          <button className="hover:text-foreground transition-colors">Help & Support</button>
          <span>•</span>
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          <span>•</span>
          <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
        </motion.div>
      </div>
    </div>
  );
}
